/**
 * Gap-fill Activity Modal.
 *
 * Opened from a gap cell in the Data Quality grid. Skips the wizard's
 * category picker entirely — the row already tells us scope, activityType,
 * subtype (fuel/vehicle/refrigerant/process), and the missing month
 * window. Those fields render as locked badges via `lockedFields` so the
 * user just fills in quantity + unit + region and submits.
 *
 * Reuses `ActivityFields` (same widget the asset wizard uses) and the
 * caller's `onPersistActivity` callback (same path as the existing
 * "Add Activity" button), so this surface stays cheap to maintain.
 */

import { useEffect, useState } from 'react';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { Button } from '@g17eco/ui-modern/components/ui/button';
import { toastFromError } from '@g17eco/molecules/toasts';
import { ActivityFields } from '../../form/ActivityFields';
import {
  isActivityFormComplete,
  type AssetActivityEntry,
} from '../../form/types';
import {
  type ActivityType,
  type ActivityUnit,
  type EmissionAsset,
  EmissionScope,
  type FuelType,
  type FugitiveGasType,
  type ProcessType,
  type RefrigerantGasType,
  type SourceSubTypeField,
  type VehicleType,
} from '../../../types/emissions';
import { useGetSettingsQuery } from '../../../api/emissions';

export interface GapFillContext {
  /** Row metadata — what activity type / subtype the row represents. */
  scope: EmissionScope;
  activityType: ActivityType;
  subTypeField: SourceSubTypeField;
  subTypeValue: string | null;
  /** Display label for the modal title (e.g. "Natural Gas — Stationary Combustion"). */
  title: string;
  /** The bucket window — "YYYY-MM" works directly with `<input type="month">`. */
  periodStart: string;
  periodEnd: string;
}

interface GapFillActivityModalProps {
  isOpen: boolean;
  onClose: () => void;
  asset: EmissionAsset;
  initiativeId: string;
  context: GapFillContext | null;
  /**
   * Persist the activity via the call site's API mutation. Mirrors the same
   * shape the existing AddActivity flow uses, so this modal can call into
   * the dashboard's existing handler without contortions.
   */
  onPersistActivity: (activity: AssetActivityEntry) => Promise<{ activityId: string }>;
}

/**
 * Build the seed entry from the gap context. Fills only the fields the row
 * implies (scope, activityType, subtype, period). The user fills the rest.
 */
const buildSeedEntry = (context: GapFillContext, asset: EmissionAsset, defaultRegion: string | undefined): AssetActivityEntry => {
  const entry: AssetActivityEntry = {
    sourceLabel: context.title,
    scope: context.scope,
    activityType: context.activityType,
    region: asset.region || defaultRegion || '',
    periodStart: context.periodStart,
    periodEnd: context.periodEnd,
    name: asset.name ? `${asset.name} — ${context.title}` : context.title,
    quantity: '',
    unit: '' as ActivityUnit,
    fuelType: '' as FuelType,
    vehicleType: '' as VehicleType,
    refrigerantType: '' as RefrigerantGasType | FugitiveGasType,
    processType: '' as ProcessType,
    regionFallback: true,
    yearFallback: true,
    dataSource: '',
    notes: '',
  };
  // Why: the API surface types subtype values as plain strings; the activity
  // entry types them as their specific enums. The server only ever emits
  // values from those enums, so casting at this single boundary is safe.
  if (context.subTypeValue) {
    switch (context.subTypeField) {
      case 'fuelType':
        entry.fuelType = context.subTypeValue as FuelType;
        break;
      case 'vehicleType':
        entry.vehicleType = context.subTypeValue as VehicleType;
        break;
      case 'refrigerantType':
        entry.refrigerantType = context.subTypeValue as RefrigerantGasType | FugitiveGasType;
        break;
      case 'processType':
        entry.processType = context.subTypeValue as ProcessType;
        break;
    }
  }
  return entry;
};

/** The set of fields that should render as locked badges in `ActivityFields`. */
const buildLockedFields = (context: GapFillContext): Set<string> => {
  const locked = new Set<string>(['scope', 'activityType', 'periodStart', 'periodEnd']);
  if (context.subTypeField && context.subTypeValue) {
    locked.add(context.subTypeField);
  }
  return locked;
};

export const GapFillActivityModal = ({
  isOpen,
  onClose,
  asset,
  initiativeId,
  context,
  onPersistActivity,
}: GapFillActivityModalProps) => {
  const { data: settings } = useGetSettingsQuery({ initiativeId }, { skip: !initiativeId || !isOpen });

  const [entry, setEntry] = useState<AssetActivityEntry | null>(null);
  const [isPersisting, setIsPersisting] = useState(false);

  // Re-seed when the modal reopens or the gap context changes — opening a
  // different gap has to wipe the previous entry's quantity/unit, otherwise
  // the user would carry stale numbers across rows.
  useEffect(() => {
    if (isOpen && context) {
      setEntry(buildSeedEntry(context, asset, settings?.defaultRegion));
    } else if (!isOpen) {
      setEntry(null);
    }
  }, [isOpen, context, asset, settings?.defaultRegion]);

  if (!context) return null;

  const lockedFields = buildLockedFields(context);

  const handleSave = async () => {
    if (!entry) return;
    setIsPersisting(true);
    try {
      await onPersistActivity(entry);
      onClose();
    } catch (error) {
      toastFromError(error);
    } finally {
      setIsPersisting(false);
    }
  };

  const canSave = entry ? isActivityFormComplete(entry) : false;

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered size='xl' scrollable>
      <ModalHeader toggle={onClose}>
        Add — {context.title}
      </ModalHeader>
      <ModalBody>
        {entry && (
          <ActivityFields
            values={entry}
            // Why: `ActivityFields.onUpdate` types its argument as a generic
            // `Partial<ActivityFieldValues>` that includes empty-string
            // sentinels for the enum fields; the caller's entry uses the
            // strict enums. The cast is local + safe — `ActivityFields`
            // never emits values that don't match the active activityType.
            onUpdate={(updates) =>
              setEntry((prev) => (prev ? { ...prev, ...(updates as Partial<AssetActivityEntry>) } : prev))
            }
            defaultDetailsOpen={false}
            lockedFields={lockedFields}
          />
        )}
      </ModalBody>
      <ModalFooter>
        <Button variant='secondary' onClick={onClose} disabled={isPersisting}>
          Cancel
        </Button>
        <Button
          className='tw:bg-emerald-700 tw:hover:bg-emerald-800 tw:text-white tw:border-0'
          onClick={handleSave}
          disabled={!canSave || isPersisting}
        >
          {isPersisting ? 'Saving...' : 'Add Activity'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};
